import axios from "axios";
import { updateField, getField } from "vuex-map-fields";
import i18n from "../i18n";
import { ISO_COUNTRY_FR } from "../common/const";

const getDefaultState = () => ({
	clinicData: {},
	clinicServices: undefined,
	allServices: [],
	allTokens: []
});

export const tiersPermissions = {
	OUTGOING_CALLS: 1,
	TRANSFER_TO_EXTENSIONS : 2,
	MONO_GROUP: 3
};

export default {
	getters: {
		getField,
		clinicData: (state) => state.clinicData,
		clinicServices: (state) => state.clinicServices,
		clinicName: (state) => state.clinicName,
		allServices: (state) => state.allServices,
		isFrance: (state) => state.clinicLocation == ISO_COUNTRY_FR,
		timeZone: (state) => state.timeZone,
		country: (state) => state.clinicLocation,
		isSector: (state) => (sector) => state.clinicData?.sector === sector,
		hasMultipleNumbers: (state) => state.clinicData.options?.twilio?.hasMultipleNumbers,
		hasWebsite: (state) => !!state.clinicData.url,
		webCallsEnabled: (state) => !!state.clinicData.options?.twilio?.webCallsEnabled,
		callQueueEnabled: (state) => !!state.clinicData.options?.voiceQueue?.enabled,
		messagingApiVersion: (state) => state.clinicData.messagingApiVersion ?? 1,
		tiersPermissions: (state) => state.tiersPermissions,
		enableExtTransfer:(state) => state.tiersPermissions?.includes(tiersPermissions.TRANSFER_TO_EXTENSIONS) ?? false,
		enableMultipleGroups: (state) => !(state.tiersPermissions?.includes(tiersPermissions.MONO_GROUP) ?? false),
		enableOutgoingCalls: (state) => state.tiersPermissions?.includes(tiersPermissions.OUTGOING_CALLS) ?? true
	},
	mutations: {
		updateField,
		setCurrent: (state, clinic) => {
			const hiddenFields = ["timeZone", "calendarId", "clientId"];
			const filteredClinic = {};

			state.clinicName = clinic.name["fr-FR"];
			state.clinicLogo = clinic.name.logo;
			state.clinicLocation = clinic.country;
			state.clientId = clinic.clientId;
			state.timeZone = clinic.timeZone;

			// Remove fields from clinic data
			for (const prop in clinic) {
				if (prop in clinic && !hiddenFields.includes(prop)) {
					filteredClinic[prop] = clinic[prop];
				}
			}

			state.clinicData = filteredClinic;
		},
		setClinicServices: (state, payload) => (state.clinicServices = payload),
		setAllServices: (state, payload) => (state.allServices = payload),
		resetState(state) {
			Object.assign(state, getDefaultState());
		},
		setAllTokens: (state, allTokens) => (state.allTokens = allTokens),
		setTiersPermissions: (state, permissions) => (state.tiersPermissions = permissions),
		setDisallowLoginOutsideWorkHours: (state, payload) => {
			if (!state.clinicData.options) {
				state.clinicData.options = {};
			}

			if (!state.clinicData.options.auth) {
				state.clinicData.options.auth = {};
			}

			state.clinicData.options.auth.disallowLoginOutsideWorkHours = payload;
		},
		setDisallowLoginOutsideOffice: (state, payload) => {
			if (!state.clinicData.options) {
				state.clinicData.options = {};
			}

			if (!state.clinicData.options.auth) {
				state.clinicData.options.auth = {};
			}

			state.clinicData.options.auth.disallowUserLoginOutsideOffice = payload;
		},

		setNotesPrivateByDefault: (state, payload) => {
			if (!state.clinicData.options) {
				state.clinicData.options = {};
			}

			state.clinicData.options.notesPrivateByDefault = payload;
		}
	},
	actions: {
		async getClinicData({ commit, rootGetters, dispatch }, id) {
			try {
				const clinicId = id || rootGetters["login/profile"].clinic;

				if (!clinicId) return;

				const { data: clinic } = await axios.get(`organizations/${clinicId}`);

				dispatch("getClinicServices", clinic);
				dispatch("getTiersPermissions", clinicId);

				commit("setCurrent", clinic);
			} catch (err) {
				if (rootGetters["login/connected"]) {
					commit("alerts/add", {
						type: "error",
						message: i18n.t("error.get-clinic-data"),
						timeout: true
					}, {
						root: true
					});
				}
			}
		},

		async getClinicServices({ commit }, clinic) {
			const { data } = await axios.get(`services?sector=${clinic.sector}`);

			const clinicServices = data.filter((service) => clinic?.services?.includes(service.id));

			commit("setClinicServices", clinicServices);
		},

		async getServicesBySector({ commit }, sector) {
			const { data } = await axios.get(`services?sector=${sector}`);

			commit("setAllServices", data);
		},

		async updateWebCalls({ state, rootGetters, commit, dispatch }, isEnabled) {
			try {
				const clinicId = rootGetters["login/profile"].clinic;

				if (!clinicId) return;

				await axios.put("organizations", {
					options: {
						twilio: {
							webCallsEnabled: isEnabled
						}
					}
				});

				dispatch("getClinicData");
			} catch (err) {
				console.error(err);
				commit(
					"alerts/add", {
						type: "error",
						message: i18n.t("error.update-web-calls"),
						timeout: true
					}, {
						root: true
					}
				);
			}
		},

		async fetchTokens({ rootGetters, commit }) {
			try {
				   const clinicData = rootGetters["clinic/clinicData"];
				   const sector = clinicData?.sector;

				   if (!sector) {
					   commit("setAllTokens", []);
					   return;
				   }

				   let { data: allTokens } = await axios.get(
					   `communication-templates/tokens?sector=${sector}`
				   );

				   allTokens = allTokens.filter(token => token.value !== "[Lien clinique]");

				   allTokens = allTokens.filter((token) => {
					   if (!clinicData.url) {
						   return i18n.t(token.description["en-CA"]) !== i18n.t("link");
					   }
					   return true;
				   });

				   if (rootGetters["clinic/isFrance"]) {
					   allTokens = allTokens.filter(
						   (token) =>
							   token.description["fr-FR"] !== "Lien du questionnaire médical" &&
							   token.description["fr-FR"] !== "Lien du questionnaire covid"
					   );
				   }
				   
				   commit("setAllTokens", allTokens);
			} catch (err) {
				console.error(err);
				if (rootGetters["login/connected"]) {
					commit(
						"alerts/add",
						{
							type: "error",
							message: i18n.t("error.get-tokens"),
							timeout: true
						},
						{ root: true }
					);
				}
			}
		},

		async getTiersPermissions({ commit, rootGetters }, id) {
			try {
				const clinicId = id || rootGetters["login/profile"].clinic;

				if (!clinicId) {
					commit("setTiersPermissions", null);
					return;
				}

				const { data: tier } = await axios.get(`tiers/organization/${clinicId}`);

				if (tier) {
					commit("setTiersPermissions", tier.permissions);
				} else {
					commit("setTiersPermissions", null);
				}
			} catch (err) {
				if (err.response?.status !== 404) {
					commit("alerts/add", {
						type: "error",
						message: i18n.t("error.get-tiers-data"),
						timeout: true
					}, {
						root: true
					});
				}
			}
		}
	},
	namespaced: true,
	state: () => ({
		clinicData: undefined,
		clinicServices: undefined,
		allServices: [],
		clinicLogo: undefined,
		clinicName: undefined,
		clinicLocation: undefined,
		timeZone: undefined,
		clientId: undefined,
		allTokens: [],
		tiersPermissions: null
	}),
	init({ watch, login, dispatch, commit }) {
		watch(
			() => login.profile?.id,
			(newId, oldId) => {
				if (newId === oldId) return;
				if (!newId && oldId) {
					commit("resetState");
				} else if (newId) {
					dispatch("getClinicData");
					dispatch("fetchTokens");
				}
			}
		);

		watch(
			() => login.refreshed,
			(refreshed) => {
				if (refreshed) {
					dispatch("getClinicData");
					dispatch("fetchTokens");
				}
			}
		);
	}
};
